import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const EditPartnerModal = ({ partner, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    industry: '',
    status: '',
    taxId: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
    notes: '',
    emailVerified: false,
    twoFactorEnabled: false,
    cesspoolConversionRewardAmount: 500,
    otherReferralRewardAmount: 50,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (partner) {
      setFormData({
        companyName: partner?.companyName || '',
        contactName: partner?.contactName || '',
        email: partner?.email || '',
        phone: partner?.phone || '',
        industry: partner?.industry || '',
        status: partner?.status || '',
        taxId: partner?.taxId || '',
        address: partner?.address || '',
        city: partner?.city || '',
        state: partner?.state || '',
        zipCode: partner?.zipCode || '',
        website: partner?.website || '',
        notes: partner?.notes || '',
        emailVerified: partner?.emailVerified || false,
        twoFactorEnabled: partner?.twoFactorEnabled || false,
        cesspoolConversionRewardAmount: partner?.cesspoolConversionRewardAmount || 500,
        otherReferralRewardAmount: partner?.otherReferralRewardAmount || 50,
      });
    }
  }, [partner]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSaving(true);
    
    try {
      // Pass updated data to parent component
      await onSave({ 
        ...partner, 
        ...formData,
        // Ensure numeric values are properly formatted
        cesspoolConversionRewardAmount: parseFloat(formData?.cesspoolConversionRewardAmount) || 0,
        otherReferralRewardAmount: parseFloat(formData?.otherReferralRewardAmount) || 0
      });
      setIsSaving(false);
      onClose();
    } catch (error) {
      console.error('Error saving partner:', error);
      alert('Failed to save partner information. Please try again.');
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const statusOptions = [
    { value: 'verified', label: 'Verified' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' },
  ];

  const industryOptions = [
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'financial-services', label: 'Financial Services' },
    { value: 'technology', label: 'Technology' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg border border-border elevation-3 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Edit" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Edit Partner</h2>
              <p className="text-sm text-muted-foreground">Update partner account details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-200"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Building" size={18} />
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Company Name"
                  type="text"
                  name="companyName"
                  value={formData?.companyName}
                  onChange={handleChange}
                  required
                />
                <Select
                  label="Industry"
                  options={industryOptions}
                  value={formData?.industry}
                  onChange={(value) => handleSelectChange('industry', value)}
                  required
                />
                <Input
                  label="Tax ID"
                  type="text"
                  name="taxId"
                  value={formData?.taxId}
                  onChange={handleChange}
                  placeholder="XX-XXXXXXX"
                />
                <Input
                  label="Website"
                  type="url"
                  name="website"
                  value={formData?.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="User" size={18} />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Contact Name"
                  type="text"
                  name="contactName"
                  value={formData?.contactName}
                  onChange={handleChange}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  name="phone"
                  value={formData?.phone}
                  onChange={handleChange}
                  required
                />
                <Select
                  label="Account Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => handleSelectChange('status', value)}
                  required
                />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="MapPin" size={18} />
                Address Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  label="Street Address"
                  type="text"
                  name="address"
                  value={formData?.address}
                  onChange={handleChange}
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="City"
                    type="text"
                    name="city"
                    value={formData?.city}
                    onChange={handleChange}
                  />
                  <Input
                    label="State"
                    type="text"
                    name="state"
                    value={formData?.state}
                    onChange={handleChange}
                    placeholder="HI"
                  />
                  <Input
                    label="ZIP Code"
                    type="text"
                    name="zipCode"
                    value={formData?.zipCode}
                    onChange={handleChange}
                    placeholder="96813"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="DollarSign" size={18} />
                Reward Amounts
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Cesspool Conversion Reward"
                  type="number"
                  name="cesspoolConversionRewardAmount"
                  value={formData?.cesspoolConversionRewardAmount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                  placeholder="500.00"
                />
                <Input
                  label="Other Referral Reward"
                  type="number"
                  name="otherReferralRewardAmount"
                  value={formData?.otherReferralRewardAmount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                  placeholder="50.00"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Set custom reward amounts for different referral types. These amounts will be used to calculate potential rewards on the partner's dashboard.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Settings" size={18} />
                Account Settings
              </h3>
              <div className="space-y-3">
                <Checkbox
                  label="Email Verified"
                  description="Partner has verified their email address"
                  name="emailVerified"
                  checked={formData?.emailVerified}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Two-Factor Authentication"
                  description="Partner has enabled 2FA for enhanced security"
                  name="twoFactorEnabled"
                  checked={formData?.twoFactorEnabled}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Admin Notes
              </label>
              <textarea
                name="notes"
                value={formData?.notes}
                onChange={handleChange}
                placeholder="Add internal notes about this partner..."
                rows={4}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-200"
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <p className="text-sm text-muted-foreground">
            Salesforce ID: <span className="font-data text-foreground">{partner?.salesforceId}</span>
          </p>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSaving}
              iconName="Check"
              iconPosition="left"
              onClick={handleSubmit}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPartnerModal;